import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Reflects an in-page wizard step as a unique URL sub-path so analytics can
 * track drop-offs per step. The host component is never remounted: it only
 * works when the host route is declared as a splat (e.g. `path="sign-up/*"`),
 * so every sub-path resolves to the same matched route element.
 *
 * @param anchorSegment The path segment the host route is mounted on
 *   (e.g. `sign-up`, `payment-information`). The base path is derived from the
 *   current location up to and including this segment, which keeps role and
 *   brand prefixes (e.g. `/Patient/`, `/kare-mobile/`) intact.
 * @param stepSegment The sub-path for the current step, or `null` for the base
 *   step (no sub-path).
 */
const useStepUrl = (anchorSegment: string, stepSegment: string | null): void => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const anchorIndex = location.pathname.indexOf(`/${anchorSegment}`);
    if (anchorIndex === -1) return;

    const afterAnchor = location.pathname[anchorIndex + anchorSegment.length + 1];
    if (afterAnchor !== undefined && afterAnchor !== '/') return;

    const basePath = location.pathname.slice(0, anchorIndex + anchorSegment.length + 1);
    const targetPath = stepSegment ? `${basePath}/${stepSegment}` : basePath;

    if (location.pathname === targetPath) return;

    navigate({ pathname: targetPath, search: location.search }, { replace: true });
  }, [anchorSegment, stepSegment, location.pathname, location.search, navigate]);
};

export default useStepUrl;
